import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AlertTriangle, Receipt, Trash2 } from "lucide-react";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { QuietField } from "../../components/QuietField";
import { api } from "../../api/client";
import type { InvoiceRequest, InvoiceRequestCreate, IsoDate, Money } from "../../api/types";
import { formatMoney, formatWholeMoney, parseWholeMoney, toAmountString, toDecimal } from "../../lib/money";
import { formatTaipeiDateTime } from "../../lib/datetime";
import { groupWhileTyping } from "../../components/MoneyInput";

// 開票申請(設計規格 §3)。結案的單按一顆鈕,把「這一期要開多少、想要哪一天的
// 發票」收準確,老闆與財務當場收到通知。**這一輪一行 Amego API 都不打** ——
// 真正開票、回填發票號碼在第 15 輪。
//
// 三條規矩寫死在這裡:
//
// 1. **想要的發票日期填了過去的日期,畫面要當場說「這張要請財務手動開立並上傳」。**
//    Amego 的 `/json/f0401` 一個日期欄位都沒有(路線圖 §7.3 實地讀過文件確認),
//    發票日就是打 API 那一刻 —— **開不出過去的發票**。不把這句話講出來,使用者
//    會以為填了日期系統就會照辦,申請完就當沒事了;而財務那頭收到的,是一張
//    做不到的要求。系統照收不擋(§3.3:這一輪只負責把需求收準確),但要說清楚
//    它接下來會走哪一條路。
// 2. **金額自己送一份,不讓後端從期別讀。** 挑了期別只是把那一期算好的數字帶進來,
//    帶進來之後改得動 —— 追加與折讓是實務常態。而且那一期的金額會跟著報價單
//    總額走:讀它的話,今天提的申請明天可能就是另一個數字,而財務照著它開的
//    發票已經寄出去了。
// 3. **狀態字樣一律用後端給的 `status_label`**(設計規格 §1.4)。前端自己維護
//    一份中文對照表,兩份遲早會漂開 —— 而漂開的症狀是第 15 輪把 `ISSUED`
//    推起來的那天,畫面上直接顯示英文。

/**
 * 付款期別裡這個對話框用得到的那幾欄。
 *
 * **刻意只宣告用得到的四個欄位**,不吃整份 `PaymentTermOut`:這裡要的只是
 * 「挑得到第幾期、帶得出那一期的金額」,多宣告的每一欄都是一條日後改欄位時
 * 會無謂牽動這個檔案的線。完整的付款條件編輯在 `PaymentTerms.tsx`。
 */
export interface InvoiceRequestTerm {
  id: number;
  seq: number;
  name: string;
  /** 這一期實際要收多少,由後端依總額算好(比例期跟著總額走,尾差在最後一期)。 */
  resolved_amount: Money;
}

/**
 * 那一筆申請在 DOM 上的 id。撤銷完要找得回遞補上來的那一筆,把焦點交給它。
 */
function requestDomId(id: number): string {
  return `nq-invoice-request-${id}`;
}

/** 沒有對應任何一期時,下拉上那一格的值。空字串才不會與 id 撞在一起。 */
const NO_TERM = "";

/** 填了過去的日期時那一句。**與畫面上的字一字不差**,測試釘著它。 */
const NEEDS_MANUAL_ISSUE = "這張要請財務手動開立並上傳";

/**
 * 留空時的說明。
 *
 * 「開今天的」是這條動線的預設,也是唯一走得了 API 的那一條 ——
 * 說清楚了,使用者才知道「不填」不是漏填,而是一個明確的選擇。
 */
const ISSUE_TODAY = "留空就是開今天的:發票日就是送出申請的那一天,第 15 輪由系統自動開立";

/**
 * 今天在台北是哪一天(`YYYY-MM-DD`)。
 *
 * **不可以用 `new Date().toISOString().slice(0, 10)`** —— 那是 UTC 的日期,
 * 台北時間早上八點以前會得到昨天。凌晨填一張「今天」的發票日期,畫面就會
 * 誤判成過去的日期,當場跳出一句根本不該出現的「要請財務手開」。
 * `en-CA` 的格式正好就是 `YYYY-MM-DD`,與 `<input type="date">` 的值同一種寫法。
 */
function taipeiToday(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Taipei",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/**
 * 想要的發票日期,接下來會走哪一條路。
 *
 * 判斷與後端 `InvoiceRequest.needs_manual_issue` 是同一條:**只有早於今天的
 * 日期做不到**。空著與填今天都是「開今天的」;未來的日期不是「API 做不到」,
 * 是「還沒到那一天」—— 到了那天照樣走 API,所以不掛警語。
 *
 * 日期是 `YYYY-MM-DD` 的定長字串,直接比字典序就是比日期,不必經過 `Date`
 * (經過 `Date` 反而要先處理時區,而那正是上面那個函式在擋的坑)。
 */
export function wantedDateNote(wanted: string, today: string): { manual: boolean; text: string } {
  const value = wanted.trim();
  if (value === "") return { manual: false, text: ISSUE_TODAY };
  if (value < today) return { manual: true, text: NEEDS_MANUAL_ISSUE };
  if (value === today) return { manual: false, text: "發票日就是今天,第 15 輪由系統自動開立" };
  return { manual: false, text: "到了那一天才開得了,發票日就是開立當天" };
}

export interface InvoiceRequestDialogProps {
  open: boolean;
  quoteId: number;
  /**
   * 這張單的總額。沒挑期別時金額預設帶它 —— 沒有拆期別的單子照樣開得了發票,
   * 而那種單要開的就是整張單的金額。
   */
  total: Money;
  onClose: () => void;
}

export function InvoiceRequestDialog({ open, quoteId, total, onClose }: InvoiceRequestDialogProps) {
  const [terms, setTerms] = useState<InvoiceRequestTerm[]>([]);
  const [rows, setRows] = useState<InvoiceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  /**
   * 送出 / 撤銷失敗的那一句。**留在畫面上,不用 toast。**
   * 後端的 409 帶著下一步該做什麼(「要改成已結案才申請得了開發票」),
   * 那是要讀得完的;飄過去三秒就消失的訊息等於沒說過。
   */
  const [error, setError] = useState("");

  const [termId, setTermId] = useState<string>(NO_TERM);
  const [amount, setAmount] = useState<string>("");
  const [wantedDate, setWantedDate] = useState<string>("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  /** 正在撤銷的那一筆。同時只撤一筆,畫面才說得清楚是哪一筆在轉圈。 */
  const [cancelling, setCancelling] = useState<number | null>(null);
  /**
   * 撤銷成功之後焦點要去哪(以申請 id 指名)。`null` = 沒有下一筆了。
   *
   * 使用者剛按下的「撤銷這筆開票申請」就在被移除的那一列上,React 卸載它之後
   * `activeElement` 落回 `<body>` —— 焦點就已經在對話框外面了,而
   * `Dialog` 的自動聚焦只在 `open` 變化時跑一次,不會把它收回來。
   * 做法與明細那張表(`LineTable`)、付款條件那張表一字不差。
   */
  const focusAfterCancel = useRef<number | null>(null);
  /** 同一個 id 不會連續被指名兩次,但用計數觸發 effect 的寫法與另外兩處一致。 */
  const [cancelTick, setCancelTick] = useState(0);
  /** 沒有下一筆時的去處:「送出申請」—— 那是撤完之後唯一做得了的事。 */
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  // 用 layout effect:要在瀏覽器畫下一格之前就把焦點放好,焦點環才不會閃一下。
  useLayoutEffect(() => {
    if (cancelTick === 0) return;
    const id = focusAfterCancel.current;
    focusAfterCancel.current = null;
    const item = id === null ? null : document.getElementById(requestDomId(id));
    const next = item?.querySelector<HTMLElement>('[data-request-cancel="true"]') ?? null;
    (next ?? submitButtonRef.current)?.focus();
  }, [cancelTick]);

  // 開啟時把期別與既有的申請一起撈回來。**關著的時候一發都不打** ——
  // 這個元件是跟著編輯器一起掛載的,不擋的話每打開一張單就白撈兩支。
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    setError("");
    Promise.all([
      api.get<InvoiceRequestTerm[]>(`/quotes/${quoteId}/payment-terms`),
      api.get<InvoiceRequest[]>(`/quotes/${quoteId}/invoice-requests`),
    ])
      .then(([termRows, requestRows]) => {
        if (!alive) return;
        setTerms(Array.isArray(termRows) ? termRows : []);
        setRows(Array.isArray(requestRows) ? requestRows : []);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setTerms([]);
        setRows([]);
        setError(err instanceof Error ? err.message : "開票申請讀不到");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [open, quoteId]);

  // 每次打開都從乾淨的表單開始:金額預設是整張單的總額(沒拆期別的單就開這個數字)。
  useEffect(() => {
    if (!open) return;
    setTermId(NO_TERM);
    setAmount(formatWholeMoney(total));
    setWantedDate("");
    setNote("");
  }, [open, total]);

  /** 挑期別:把那一期算好的金額帶進來。帶進來之後改得動(追加、折讓)。 */
  const pickTerm = (raw: string) => {
    setTermId(raw);
    const picked = terms.find((term) => String(term.id) === raw);
    setAmount(formatWholeMoney(picked ? picked.resolved_amount : total));
  };

  const dateNote = wantedDateNote(wantedDate, taipeiToday());
  // 金額一律走 decimal.js,**不經過 Number / parseFloat**:金額是錢,
  // 而 JS 的浮點數在錢上面是錯的(全域規矩)。
  const amountError = toDecimal(amount).lessThanOrEqualTo(0) ? "開票金額要大於 0" : null;

  const submit = async () => {
    if (amountError !== null) return;
    setSubmitting(true);
    setError("");
    try {
      const payload: InvoiceRequestCreate = {
        payment_term_id: termId === NO_TERM ? null : Number(termId),
        amount: toAmountString(parseWholeMoney(amount)),
        // 空字串要送 null,不是 "" —— 後端的 `wanted_date` 是日期欄位,
        // 空字串會變成一個 422,而使用者做的事完全正確
        wanted_date: wantedDate.trim() === "" ? null : (wantedDate as IsoDate),
        note,
      };
      const created = await api.post<InvoiceRequest>(`/quotes/${quoteId}/invoice-requests`, payload);
      // 後端回的是落地之後的那一筆(狀態字樣、要不要手開、提出時間都在裡面),
      // 接到清單尾巴就好,不必為了一筆再撈一次整份清單。
      setRows((prev) => [...prev, created]);
      // 表單清乾淨:分期請款一次會提好幾筆,留著上一筆的金額只會讓人重複送出
      setTermId(NO_TERM);
      setAmount(formatWholeMoney(total));
      setWantedDate("");
      setNote("");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "開票申請送不出去");
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (row: InvoiceRequest) => {
    // 只有 PENDING 的那幾筆畫得出撤銷鈕,所以交棒的對象也只能在它們裡面挑。
    const cancellable = rows.filter((item) => item.status === "PENDING");
    const at = cancellable.findIndex((item) => item.id === row.id);
    const neighbour = cancellable[at + 1] ?? cancellable[at - 1] ?? null;
    setCancelling(row.id);
    setError("");
    try {
      await api.del(`/invoice-requests/${row.id}`);
      focusAfterCancel.current = neighbour?.id ?? null;
      setRows((prev) => prev.filter((item) => item.id !== row.id));
      setCancelTick((tick) => tick + 1);
    } catch (err: unknown) {
      // 失敗時那一列還在,焦點也還在它的撤銷鈕上 —— 不必安排去處。
      setError(err instanceof Error ? err.message : "撤銷失敗");
    } finally {
      setCancelling(null);
    }
  };

  const termOptions = [
    { value: NO_TERM, label: "不對應任何一期(整張單)" },
    ...terms.map((term) => ({
      value: String(term.id),
      label: `第 ${term.seq} 期 ${term.name || "(未命名)"}(${formatMoney(term.resolved_amount)})`,
    })),
  ];

  return (
    <Dialog open={open} onClose={onClose} title="開票申請" maxWidth="max-w-2xl">
      <div className="flex flex-col gap-3">
        <p className="m-0 text-sm text-[var(--text-3)]">
          申請送出後,所有啟用中的老闆與財務都會收到通知。一張單提得了多筆 —— 分期請款就是這樣。
        </p>

        <div className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
          <QuietField
            as="select"
            label="這一筆是為了哪一期"
            options={termOptions}
            value={termId}
            hint={terms.length === 0 ? "這張單還沒有拆付款期別,開的就是整張單的金額" : undefined}
            onChange={(event) => pickTerm(event.target.value)}
          />
          <QuietField
            label="開票金額"
            numeric
            inputMode="decimal"
            suffix="元"
            value={amount}
            error={amountError}
            hint="挑了期別會帶入那一期的數字,改得動 —— 追加與折讓都是實務常態"
            onChange={(event) => setAmount(groupWhileTyping(event.target.value))}
            onBlur={() => setAmount(formatWholeMoney(parseWholeMoney(amount)))}
          />
          <QuietField
            label="想要的發票日期"
            type="date"
            value={wantedDate}
            // **manual 時也要掛**(短句版;為什麼做不到留在下面那一塊)。
            // 拿掉的話,這一格的 aria-describedby 在**最需要說明的那一種情況下
            // 反而消失** —— 留空或填今天聽得到說明,填了過去的日期什麼都聽不到,
            // 而那正是整個對話框最重要的一句話。
            hint={dateNote.text}
            onChange={(event) => setWantedDate(event.target.value)}
          />
          <QuietField
            label="備註"
            value={note}
            maxLength={255}
            placeholder="要給財務的說明"
            onChange={(event) => setNote(event.target.value)}
          />
        </div>

        {/* 填了過去的日期。**這一段是整個對話框最重要的一句話**(路線圖 §7.3):
            開立發票的 API 沒有日期欄位,發票日就是打 API 那一刻,所以過去的
            日期系統做不到。不講出來的話,使用者會以為申請完就沒事了,而財務
            那頭收到的是一張做不到的要求。圖示 + 文字,不靠顏色說話。

            **外面這個 `role="status"` 一直都在,換掉的只有裡面的內容。**
            live region 要先存在於 DOM、之後內容才變動,螢幕閱讀器才播得可靠;
            整塊連同內容一起新掛上去的 region,多數螢幕閱讀器根本不播 ——
            那就等於沒說。沒有警語時它是一個空的、零高度的節點。 */}
        <div role="status" aria-label="想要的發票日期會怎麼開">
          {dateNote.manual ? (
            <div className="flex items-start gap-2 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface-2)] px-3 py-2">
              <AlertTriangle aria-hidden="true" size={16} className="mt-0.5 shrink-0 text-[var(--warn)]" />
              <div className="min-w-0 flex-1">
                <p className="m-0 text-sm font-semibold text-[var(--text)]">{NEEDS_MANUAL_ISSUE}</p>
                <p className="m-0 text-sm text-[var(--text-2)]">
                  開立發票的 API 沒有日期欄位,發票日就是送出的那一刻 ——
                  過去的日期系統開不出來,不會因為填了就照辦。申請照樣送得出去,只是這一張要由財務在後台手開、
                  把檔案與號碼填回系統。
                </p>
              </div>
            </div>
          ) : null}
        </div>

        {error === "" ? null : (
          <p role="alert" className="m-0 text-sm text-[var(--danger)]">
            {error}
          </p>
        )}

        <div>
          <Button
            ref={submitButtonRef}
            variant="primary"
            icon={<Receipt aria-hidden="true" size={16} />}
            disabled={amountError !== null}
            loading={submitting}
            onClick={() => void submit()}
          >
            送出申請
          </Button>
        </div>

        <h3 className="m-0 mt-1 text-sm font-semibold text-[var(--text)]">這張單的開票申請</h3>
        {loading ? (
          <p className="m-0 text-sm text-[var(--text-3)]">讀取開票申請…</p>
        ) : rows.length === 0 ? (
          <p className="m-0 text-sm text-[var(--text-3)]">還沒有開票申請</p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {rows.map((row) => (
              <li
                key={row.id}
                id={requestDomId(row.id)}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface-2)] px-2 py-2"
              >
                <span className="tabular text-sm font-medium text-[var(--text)]">
                  {formatMoney(row.amount)} 元
                </span>
                {/* 狀態一律帶文字,而且字樣是後端給的(設計規格 §1.4)。 */}
                <span className="text-sm text-[var(--text-2)]">{row.status_label}</span>
                {row.payment_term_seq === null ? null : (
                  <span className="text-sm text-[var(--text-3)]">
                    第 {row.payment_term_seq} 期 {row.payment_term_name}
                  </span>
                )}
                {row.needs_manual_issue ? (
                  <span className="inline-flex items-center gap-1 text-sm text-[var(--text-2)]">
                    <AlertTriangle aria-hidden="true" size={14} className="shrink-0 text-[var(--warn)]" />
                    要財務手開
                  </span>
                ) : null}
                <span className="text-sm text-[var(--text-3)]">
                  {row.requester_name || "已離職的同仁"} · {formatTaipeiDateTime(row.requested_at)}
                </span>
                {row.status === "PENDING" ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="ml-auto"
                    icon={<Trash2 aria-hidden="true" size={16} />}
                    // 已經開出去的那一筆撤不得(後端 409)。這裡整顆不畫 ——
                    // 畫一顆永遠按不動的鈕,只會讓人一直想它為什麼按不動。
                    // 撤完之後靠它找回遞補上來的那一筆(見 `focusAfterCancel`)
                    data-request-cancel="true"
                    aria-label="撤銷這筆開票申請"
                    loading={cancelling === row.id}
                    onClick={() => void cancel(row)}
                  >
                    撤銷
                  </Button>
                ) : null}
                {/* 這筆申請的發票資訊(業主 2026-10-15):開了幾張就列幾張;還沒開寫「申請中」 */}
                <div data-request-invoices="true" className="w-full text-sm text-[var(--text-2)]">
                  {(row.invoices ?? []).length === 0 ? (
                    <span className="text-[var(--warn)]">{row.status === "PENDING" ? "申請中,財務還沒開" : row.status_label}</span>
                  ) : (
                    <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                      {(row.invoices ?? []).map((inv) => (
                        <li key={inv.id} className="tabular">
                          發票 {inv.invoice_no || "—"} · {inv.invoice_date ?? "—"} · {formatMoney(inv.amount)} 元 · {inv.status_label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}
